"use client";

import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { ReactComponent as EventsIcon } from "../../assets/icons/events_icon.svg";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useBodyLock } from "../../hooks/useDisableBodyScroll";
import EventMetaIcon from "./EventMetaIcon";
import { buildEventPaymentSummary, isEventTicketPresentation } from "./eventPaymentSummary";
import { formatPhysicalEventLocation } from "./eventLocation";
import { formatEventMoney } from "../../utils/eventCurrency";
import WrappedSeparatedText from "./WrappedSeparatedText";
import { isConceptAwaitingDetails } from "./conceptPresentation";

const TRANSLATION_LANGS = new Set(["en", "ru", "sk"]);
const DESCRIPTION_FALLBACK_LINES = 4;
// Metadata shares this flexible slot with the description. Four lines keep
// every event detail visible while short descriptions leave their spare space
// below the complete description-and-metadata group.
const DESCRIPTION_MAX_LINES = 4;
const DESCRIPTION_LINE_HEIGHT_PX = 18;
const INLINE_TRANSLATE_BUTTON_CLASS =
  "ml-[4px] inline-flex h-[18px] w-[76px] shrink-0 translate-y-[-1px] items-center justify-center gap-[4px] rounded-[18px] bg-[#13C6EE]/30 px-[10px] align-baseline text-[9px] font-medium leading-[1.2] text-white";
const MIN_TRANSLATABLE_DESCRIPTION_WORDS = 6;
const BACKGROUND_SCROLL_DISMISS_THRESHOLD_PX = 8;
const PREVIEW_ACTION_BOTTOM_CSS =
  "calc(var(--bottom-nav-height,72px) + max(env(safe-area-inset-bottom,0px),10px) + 8px)";
const GUEST_PREVIEW_ACTION_BOTTOM_CSS =
  "calc(max(env(safe-area-inset-bottom,0px),10px) + 8px)";
const PREVIEW_SHEET_MAX_HEIGHT_CSS = "min(480px, calc(100dvh - 120px))";
const PREVIEW_ACTIONS_MARGIN_TOP_PX = 12;
const SOCIAL_FIT_CHIP_CLASS_BY_STATUS = {
  strong: "bg-emerald-300/92 text-[#072116]",
  good: "bg-amber-200/92 text-[#2a1d05]",
  low: "bg-orange-300/92 text-[#2b1200]",
  outside: "bg-rose-200/92 text-[#2a0710]",
  learning: "bg-sky-200/92 text-[#10213d]",
};
const PAYMENT_FIELDS = [
  "payment_category_1",
  "payment_category_2",
  "payment_category_3",
  "payment_category_4",
];

function normalizeTranslationLang(raw) {
  const value = String(raw || "").trim().toLowerCase().split("-")[0];
  return TRANSLATION_LANGS.has(value) ? value : "en";
}

function showTranslationError(message) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("global-error", {
      detail: {
        message:
          message ||
          "Event description translation is temporarily unavailable. Please try again.",
        status: 503,
      },
    })
  );
}

function parseAmount(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseFloat(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function getSharedCostTotal(event) {
  const total = PAYMENT_FIELDS.reduce((sum, field) => {
    const amount = parseAmount(event?.[field]);
    return amount && amount > 0 ? sum + amount : sum;
  }, 0);
  return total > 0 ? total : null;
}

function getTicketAmount(event) {
  const preview = event?.ticket_purchase_preview || event?.participant_ticketing || {};
  const candidates = [
    preview.full_amount,
    preview.total_amount,
    preview.join_checkout_amount,
    event?.ticket_price,
    event?.price,
    event?.payment_category_1,
  ];

  for (const candidate of candidates) {
    const amount = parseAmount(candidate);
    if (amount && amount > 0) return amount;
  }

  return null;
}

function estimateChipWidth(label, hasIcon = false) {
  const text = String(label || "");
  return Math.max(hasIcon ? 78 : 68, Math.ceil(text.length * 6.6) + (hasIcon ? 52 : 38));
}

function fitChipsInOneRow(chips, maxWidth = 342) {
  const fitted = [];
  let used = 0;
  for (const chip of chips) {
    const width = chip.width || estimateChipWidth(chip.label, Boolean(chip.iconSrc));
    const nextUsed = used + width + (fitted.length ? 3 : 0);
    if (nextUsed > maxWidth) continue;
    fitted.push({ ...chip, width });
    used = nextUsed;
    if (fitted.length >= 3) break;
  }
  return fitted;
}

function isBusinessHostEvent(event) {
  const hostFormatNumber = Number(event?.host_format);
  return Number.isFinite(hostFormatNumber) && hostFormatNumber !== 0;
}

function buildSocialPreferenceFitChip(event, t) {
  const fit = event?.social_preference_fit || null;
  if (fit?.is_within_audience === false) return null;
  const status = String(fit?.status || "").trim().toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(SOCIAL_FIT_CHIP_CLASS_BY_STATUS, status)) {
    return null;
  }
  return {
    id: "social-preference-fit",
    label: t(`event_description_modal.chips.social_fit.${status}`, {
      defaultValue: fit?.label || "Social Preference Fit",
    }),
    className: SOCIAL_FIT_CHIP_CLASS_BY_STATUS[status],
  };
}

function buildPreviewChips(event, t, lang) {
  const socialFitChip = buildSocialPreferenceFitChip(event, t);
  if (socialFitChip) {
    return [socialFitChip];
  }

  const pricingModel = String(event?.pricing_model || "").toLowerCase();
  const feeType = Number(event?.fee_type ?? 0);
  const chips = [];
  const isOnline = Number(event?.event_format) === 0;
  const awaitingConceptDetails = isConceptAwaitingDetails(event);

  if (feeType !== 1 || pricingModel === "free") {
    if (!awaitingConceptDetails) {
      chips.push({
        id: "cost",
        label: t("event_description_modal.chips.free", { defaultValue: "Free" }),
        iconSrc: "/assets/events/payable_icon.svg",
      });
    }
  } else if (isEventTicketPresentation(event)) {
    const amount = formatEventMoney(getTicketAmount(event), event, lang);
    chips.push({
      id: "cost",
      label: amount
        ? t("event_description_modal.chips.ticket_with_amount", {
            amount,
            defaultValue: "Ticket: {{amount}}",
          })
        : t("event_description_modal.chips.ticket", { defaultValue: "Ticket" }),
      iconSrc: "/assets/events/payable_icon.svg",
    });
  } else {
    const amount = formatEventMoney(getSharedCostTotal(event) || getTicketAmount(event), event, lang);
    chips.push({
      id: "cost",
      label: amount
        ? t("event_description_modal.chips.shared_cost_with_amount", {
            amount,
            defaultValue: "Shared Cost: {{amount}}",
          })
        : t("event_description_modal.chips.shared_cost", {
            defaultValue: "Shared Cost",
          }),
      iconSrc: "/assets/events/payable_icon.svg",
    });
  }

  if (!awaitingConceptDetails) {
    chips.push({
      id: "format",
      label: isOnline
        ? t("events.details.online", { defaultValue: "Online" })
        : t("events.details.offline", { defaultValue: "Offline" }),
      iconSrc: isOnline ? "/assets/events/online_icon.svg" : "/assets/events/offline_icon.svg",
    });
  }

  const targetGender = String(event?.target_gender || "").toLowerCase();
  if (targetGender === "female") {
    chips.push({
      id: "gender",
      label: t("event_description_modal.chips.female_only", {
        defaultValue: "Female only",
      }),
      iconSrc: "/assets/events/female_event.svg",
    });
  } else if (targetGender === "male") {
    chips.push({
      id: "gender",
      label: t("event_description_modal.chips.male_only", {
        defaultValue: "Male only",
      }),
      iconSrc: "/assets/events/male_event.svg",
    });
  } else {
    const min = parseAmount(event?.target_age_min);
    const max = parseAmount(event?.target_age_max);
    let age = "";
    if (Number.isFinite(min) && Number.isFinite(max)) age = `${min}-${max}`;
    else if (Number.isFinite(min)) age = `${min}+`;
    else if (Number.isFinite(max)) age = `<=${max}`;
    if (age) {
      chips.push({
        id: "age",
        label: t("event_description_modal.chips.age", {
          age,
          defaultValue: "Age {{age}}",
        }),
        iconSrc: "/assets/events/age_icon.svg",
      });
    } else if (awaitingConceptDetails) {
      chips.push({
        id: "age",
        label: t("discovery_filters.any_age", { defaultValue: "Any age" }),
        iconSrc: "/assets/events/age_icon.svg",
      });
    }
  }

  chips.push({
    id: "host-kind",
    label: isBusinessHostEvent(event)
      ? t("event_description_modal.chips.club_host_short", { defaultValue: "Club" })
      : t("event_description_modal.chips.personal_host_short", { defaultValue: "Host" }),
    iconSrc: isBusinessHostEvent(event)
      ? "/assets/events/organization_icon.svg"
      : "/assets/events/personal_icon.svg",
  });

  if (event?.is_verified) {
    chips.push({
      id: "verified",
      label: t("events.details.official_event", { defaultValue: "Verified" }),
      iconSrc: "/assets/events/verified_icon.svg",
    });
  }

  return fitChipsInOneRow(chips);
}

function formatDuration(minutes, t) {
  const duration = parseAmount(minutes);
  if (!duration || duration <= 0) {
    return t("event_description_modal.meta.duration_unknown", {
      defaultValue: "Duration not specified",
    });
  }

  if (duration < 60) {
    return t("event_description_modal.meta.duration_minutes", {
      minutes: Math.round(duration),
      defaultValue: "About {{minutes}} min",
    });
  }

  const low = Math.floor(duration / 60);
  const high = Math.ceil(duration / 60);
  if (low === high) {
    if (low === 1) {
      return t("event_description_modal.meta.duration_hour", {
        defaultValue: "About 1 hour",
      });
    }
    return t("event_description_modal.meta.duration_hours", {
      count: low,
      defaultValue: "About {{count}} hours",
    });
  }

  return t("event_description_modal.meta.duration_hours_range", {
    min: low,
    max: high,
    defaultValue: "About {{min}}-{{max}} hours",
  });
}

function formatEventDate(dateTime, t, lang) {
  if (!dateTime) {
    return t("event_description_modal.meta.date_unknown", {
      defaultValue: "Date not specified",
    });
  }

  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) {
    return t("event_description_modal.meta.date_unknown", {
      defaultValue: "Date not specified",
    });
  }

  const datePart = new Intl.DateTimeFormat(lang || "en", {
    day: "numeric",
    month: "long",
  }).format(date);
  const timePart = new Intl.DateTimeFormat(lang || "en", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

  return t("event_description_modal.meta.date_at_time", {
    date: datePart,
    time: timePart,
    defaultValue: "{{date}} at {{time}}",
  });
}

function formatAddress(event, t, language) {
  if (Number(event?.event_format) === 0) {
    return t("events.details.online_label", { defaultValue: "Online" });
  }

  return formatPhysicalEventLocation(
    event,
    t("events.details.show_on_map", { defaultValue: "Show on map" }),
    language
  );
}

function normalizeDescriptionText(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

function getDescriptionLineCapacity(height) {
  const numericHeight = Number(height);
  if (!Number.isFinite(numericHeight) || numericHeight <= 0) {
    return DESCRIPTION_FALLBACK_LINES;
  }

  return Math.min(
    DESCRIPTION_MAX_LINES,
    Math.max(1, Math.floor(numericHeight / DESCRIPTION_LINE_HEIGHT_PX))
  );
}

const EventDescriptionModal = ({
  event = null,
  isGuest = false,
  description = "",
  loading = false,
  onExpand = () => {},
  onClose = () => {},
  onHeight = () => {},
  onShowMap = null,
  directionsUrl = "",
  isMapContext = false,
  isConcept = false,
}) => {
  const sheetRef = useRef(null);
  const descriptionSlotRef = useRef(null);
  const descriptionMeasureRef = useRef(null);
  const descriptionMeasureTextRef = useRef(null);
  const descriptionMeasureEllipsisRef = useRef(null);
  const startYRef = useRef(null);
  const backgroundTouchStartRef = useRef(null);
  const isDesktop = useIsDesktop();
  useBodyLock(isDesktop);

  const { t, i18n } = useTranslation("events");
  const lang = normalizeTranslationLang(i18n.resolvedLanguage || i18n.language);
  const eventId = event?.id;
  const originalText = String(description || event?.description || "");

  const [translationLoading, setTranslationLoading] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  const [translationMap, setTranslationMap] = useState({});
  const [descriptionLineCapacity, setDescriptionLineCapacity] = useState(
    DESCRIPTION_FALLBACK_LINES
  );
  const [inlineDescriptionLayout, setInlineDescriptionLayout] = useState({
    source: "",
    lines: DESCRIPTION_FALLBACK_LINES,
    text: "",
    truncated: false,
  });

  useEffect(() => {
    setTranslationLoading(false);
    setShowTranslated(false);
    setTranslationMap({});
  }, [eventId, originalText]);

  useEffect(() => {
    if (isDesktop) return undefined;

    const resetBackgroundTouch = () => {
      backgroundTouchStartRef.current = null;
    };
    const handleBackgroundTouchStart = (e) => {
      const touch = e.touches?.[0];
      if (!touch || sheetRef.current?.contains(e.target)) {
        resetBackgroundTouch();
        return;
      }
      backgroundTouchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    };
    const handleBackgroundTouchMove = (e) => {
      const start = backgroundTouchStartRef.current;
      const touch = e.touches?.[0];
      if (!start || !touch) return;

      const deltaX = Math.abs(touch.clientX - start.x);
      const deltaY = Math.abs(touch.clientY - start.y);
      if (
        deltaY < BACKGROUND_SCROLL_DISMISS_THRESHOLD_PX ||
        deltaY < deltaX
      ) {
        return;
      }

      resetBackgroundTouch();
      onClose();
    };
    const handleBackgroundWheel = (e) => {
      if (
        sheetRef.current?.contains(e.target) ||
        Math.abs(e.deltaY) < BACKGROUND_SCROLL_DISMISS_THRESHOLD_PX
      ) {
        return;
      }
      onClose();
    };

    window.addEventListener("touchstart", handleBackgroundTouchStart, {
      passive: true,
    });
    window.addEventListener("touchmove", handleBackgroundTouchMove, {
      passive: true,
    });
    window.addEventListener("wheel", handleBackgroundWheel, { passive: true });
    window.addEventListener("touchend", resetBackgroundTouch, { passive: true });
    window.addEventListener("touchcancel", resetBackgroundTouch, { passive: true });
    return () => {
      window.removeEventListener("touchstart", handleBackgroundTouchStart);
      window.removeEventListener("touchmove", handleBackgroundTouchMove);
      window.removeEventListener("wheel", handleBackgroundWheel);
      window.removeEventListener("touchend", resetBackgroundTouch);
      window.removeEventListener("touchcancel", resetBackgroundTouch);
    };
  }, [isDesktop, onClose]);

  const handleTouchStart = useCallback((e) => {
    startYRef.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      const endY = e.changedTouches[0].clientY;
      const deltaY = endY - (startYRef.current ?? endY);

      if (deltaY < -30) onExpand();
      else if (deltaY > 80) onClose();
    },
    [onExpand, onClose]
  );

  useEffect(() => {
    const sheetEl = sheetRef.current;
    if (!sheetEl) return;
    sheetEl.addEventListener("touchstart", handleTouchStart, { passive: true });
    sheetEl.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      sheetEl.removeEventListener("touchstart", handleTouchStart);
      sheetEl.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);

  useEffect(() => {
    const sheetEl = sheetRef.current;
    if (!sheetEl) return undefined;
    let active = true;

    const report = () => {
      if (!active) return;
      const h = sheetEl.getBoundingClientRect().height;
      onHeight(h);
    };

    report();
    const ro = new ResizeObserver(report);
    ro.observe(sheetEl);
    return () => {
      active = false;
      ro.disconnect();
    };
  }, [onHeight]);

  useEffect(() => {
    const descriptionSlot = descriptionSlotRef.current;
    if (!descriptionSlot) return undefined;
    let active = true;

    const updateLineCapacity = () => {
      if (!active) return;
      const nextCapacity = getDescriptionLineCapacity(
        descriptionSlot.getBoundingClientRect().height
      );
      setDescriptionLineCapacity((currentCapacity) =>
        currentCapacity === nextCapacity ? currentCapacity : nextCapacity
      );
    };

    updateLineCapacity();
    const ro = new ResizeObserver(updateLineCapacity);
    ro.observe(descriptionSlot);
    window.addEventListener("resize", updateLineCapacity);
    return () => {
      active = false;
      ro.disconnect();
      window.removeEventListener("resize", updateLineCapacity);
    };
  }, []);

  const translatedText = translationMap[lang] || "";
  const displayText = showTranslated && translatedText ? translatedText : originalText;
  const normalizedOriginalText = normalizeDescriptionText(originalText);
  const descriptionWordCount = normalizedOriginalText
    ? normalizedOriginalText.split(" ").length
    : 0;
  const sourceLang = String(event?.event_language || "")
    .trim()
    .toLowerCase()
    .split("-")[0];
  const canTranslate = Boolean(
    lang !== "ru" &&
      eventId &&
      sourceLang &&
      sourceLang !== lang &&
      descriptionWordCount >= MIN_TRANSLATABLE_DESCRIPTION_WORDS
  );
  const chips = useMemo(
    () => buildPreviewChips(event, t, lang),
    [event, t, lang]
  );

  const address = useMemo(() => formatAddress(event, t, lang), [event, t, lang]);
  const awaitingConceptDetails = isConceptAwaitingDetails(event);
  const showDirectionsAction = Boolean(isMapContext && directionsUrl);
  const showMapAction = Boolean(!showDirectionsAction && onShowMap);
  const duration = useMemo(
    () => formatDuration(event?.approximate_duration, t),
    [event?.approximate_duration, t]
  );
  const date = useMemo(
    () =>
      isConcept || event?.type === "concept"
        ? t("events.details.concept_datetime_note", {
            defaultValue: "Date and time will be set later",
          })
        : formatEventDate(event?.date_time, t, lang),
    [event?.date_time, event?.type, isConcept, t, lang]
  );
  const paymentSummary = useMemo(
    () => buildEventPaymentSummary(event, t, lang),
    [event, t, lang]
  );

  const translateButtonLabel = showTranslated
    ? t("event_description_modal.translate.original", { defaultValue: "Original" })
    : translationLoading
      ? t("event_description_modal.translate.loading", {
          defaultValue: "Translating...",
        })
      : t("event_description_modal.translate.button", { defaultValue: "Translate" });

  const previewDescription =
    normalizeDescriptionText(displayText) ||
    t(
      isConcept
        ? "event_description_modal.body.title_concept"
        : "event_description_modal.body.title_event"
    );
  const visibleDescriptionLines = Math.max(1, descriptionLineCapacity);

  useLayoutEffect(() => {
    if (!canTranslate) return undefined;

    const descriptionSlot = descriptionSlotRef.current;
    const measure = descriptionMeasureRef.current;
    const measureText = descriptionMeasureTextRef.current;
    const measureEllipsis = descriptionMeasureEllipsisRef.current;
    if (!descriptionSlot || !measure || !measureText || !measureEllipsis) {
      return undefined;
    }

    let active = true;

    const measureCandidate = (text, truncated) => {
      measureText.textContent = text;
      measureEllipsis.hidden = !truncated;
      const rectHeight = measure.getBoundingClientRect().height;
      return Math.max(rectHeight, measure.scrollHeight);
    };

    const updateInlineDescriptionLayout = () => {
      if (!active || descriptionSlot.getBoundingClientRect().width <= 0) return;

      const maxHeight = visibleDescriptionLines * DESCRIPTION_LINE_HEIGHT_PX;
      if (measureCandidate(previewDescription, false) <= maxHeight + 0.5) {
        setInlineDescriptionLayout((current) => {
          if (
            current.source === previewDescription &&
            current.lines === visibleDescriptionLines &&
            current.text === previewDescription &&
            !current.truncated
          ) {
            return current;
          }
          return {
            source: previewDescription,
            lines: visibleDescriptionLines,
            text: previewDescription,
            truncated: false,
          };
        });
        return;
      }

      const characters = Array.from(previewDescription);
      let low = 0;
      let high = characters.length;
      let bestFit = "";

      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        const candidate = characters.slice(0, middle).join("").trimEnd();
        if (measureCandidate(candidate, true) <= maxHeight + 0.5) {
          bestFit = candidate;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }

      setInlineDescriptionLayout((current) => {
        if (
          current.source === previewDescription &&
          current.lines === visibleDescriptionLines &&
          current.text === bestFit &&
          current.truncated
        ) {
          return current;
        }
        return {
          source: previewDescription,
          lines: visibleDescriptionLines,
          text: bestFit,
          truncated: true,
        };
      });
    };

    updateInlineDescriptionLayout();
    const resizeObserver = new ResizeObserver(updateInlineDescriptionLayout);
    resizeObserver.observe(descriptionSlot);

    const fontsReady = document.fonts?.ready;
    fontsReady?.then(() => {
      if (active) updateInlineDescriptionLayout();
    });

    return () => {
      active = false;
      resizeObserver.disconnect();
    };
  }, [
    canTranslate,
    previewDescription,
    translateButtonLabel,
    visibleDescriptionLines,
  ]);

  const hasMeasuredInlineDescription =
    inlineDescriptionLayout.source === previewDescription &&
    inlineDescriptionLayout.lines === visibleDescriptionLines;
  const renderedPreviewDescription =
    canTranslate && hasMeasuredInlineDescription
      ? inlineDescriptionLayout.text
      : previewDescription;
  const isPreviewDescriptionTruncated = Boolean(
    canTranslate &&
      hasMeasuredInlineDescription &&
      inlineDescriptionLayout.truncated
  );

  const toggleTranslation = async (e) => {
    e?.stopPropagation?.();
    if (!canTranslate || translationLoading) return;

    if (showTranslated) {
      setShowTranslated(false);
      return;
    }

    if (translatedText) {
      setShowTranslated(true);
      return;
    }

    try {
      setTranslationLoading(true);
      const resp = await api.post(`/events/${eventId}/translate-description/`, {
        target_lang: lang,
      });
      const nextText = String(resp?.data?.text || "").trim();
      if (nextText) {
        setTranslationMap((prev) => ({ ...prev, [lang]: nextText }));
        setShowTranslated(true);
      }
    } catch (error) {
      const backendMessage = String(error?.response?.data?.error || "").trim();
      showTranslationError(backendMessage);
    } finally {
      setTranslationLoading(false);
    }
  };

  const stopAndRun = (handler) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    handler?.(event, e);
  };

  return (
    <div
      ref={sheetRef}
      data-testid="event-description-preview"
      className="fixed inset-x-0 bottom-0 z-40 overscroll-contain px-0"
    >
      <section
        data-testid="event-description-preview-sheet"
        className={`relative w-screen max-w-none overflow-hidden rounded-t-[45px] bg-[linear-gradient(90deg,rgba(25,25,25,0.55)_0%,rgba(27,45,97,0.55)_100%)] text-white shadow-[0_-20px_60px_rgba(0,0,0,0.28)] backdrop-blur-[37.5px] ${
          !isDesktop && isMapContext ? "ring-1 ring-white/20" : ""
        }`}
        style={{
          "--event-description-action-bottom": isGuest
            ? GUEST_PREVIEW_ACTION_BOTTOM_CSS
            : PREVIEW_ACTION_BOTTOM_CSS,
          height: PREVIEW_SHEET_MAX_HEIGHT_CSS,
          maxHeight: PREVIEW_SHEET_MAX_HEIGHT_CSS,
        }}
        onClick={onExpand}
      >
        <button
          type="button"
          className="absolute left-0 right-0 top-[10px] flex flex-col items-center"
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          aria-label={t("event_description_modal.header.tap_to_show_more")}
        >
          <span className="h-[5px] w-[100px] rounded-[35px] bg-white/25" />
          <span className="mt-[3px] text-center text-[12px] font-normal leading-4 text-white/25">
            {t("event_description_modal.header.swipe_to_show_more", {
              defaultValue: "Swipe up to show more",
            })}
          </span>
        </button>

        <div
          data-testid="event-description-preview-content"
          className="flex h-full min-h-0 flex-col px-[15px] pb-[var(--event-description-action-bottom)] pt-[44px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex w-full shrink-0 flex-nowrap items-center justify-center gap-[3px] overflow-hidden ${chips.some((chip) => chip.id === "social-preference-fit" && chip.label.includes("\n")) ? "h-[46px]" : "h-[35px]"}`}>
            {chips.map((chip) => (
              <span
                key={chip.id}
                className={`flex items-center justify-center gap-[6px] overflow-hidden rounded-[50px] bg-white/20 px-[12px] text-center text-[13px] font-bold text-white ${chip.id === "cost" ? "min-w-0 flex-1" : "shrink-0"} ${chip.id === "social-preference-fit" && chip.label.includes("\n") ? "h-[46px] whitespace-pre-line py-[6px] leading-[17px]" : "h-[35px] pb-[7px] pt-[6px] leading-[22px]"} ${chip.className || ""}`}
                style={{ width: chip.id === "social-preference-fit" ? "100%" : chip.id === "cost" ? undefined : chip.width }}
              >
                {chip.iconSrc ? (
                  <EventMetaIcon
                    src={chip.iconSrc}
                    alt=""
                    decorative
                    className="h-[17px] w-[17px]"
                  />
                ) : null}
                <span className={chip.id === "social-preference-fit" && chip.label.includes("\n") ? "whitespace-pre-line" : "whitespace-nowrap"}>{chip.label}</span>
              </span>
            ))}
          </div>

          <div
            ref={descriptionSlotRef}
            data-testid="event-description-preview-description-slot"
            className="relative mt-[15px] flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            {loading ? (
              <p className="text-[15px] leading-[1.25] text-white/70">
                {t("event_description_modal.body.loading", {
                  defaultValue: "Loading...",
                })}
              </p>
            ) : (
              <>
                <p
                  data-testid="event-description-preview-text"
                  aria-label={previewDescription}
                  className="block break-words overflow-hidden text-[15px] font-normal text-white [--event-description-line-clamp:4] [overflow-wrap:anywhere]"
                  style={{
                    "--event-description-line-clamp": visibleDescriptionLines,
                    display: "block",
                    lineHeight: `${DESCRIPTION_LINE_HEIGHT_PX}px`,
                    maxHeight: `${visibleDescriptionLines * DESCRIPTION_LINE_HEIGHT_PX}px`,
                    overflow: "hidden",
                  }}
                >
                  <span>{renderedPreviewDescription}</span>
                  {isPreviewDescriptionTruncated ? (
                    <span aria-hidden="true">…</span>
                  ) : null}
                  {canTranslate ? (
                    <button
                      type="button"
                      onClick={toggleTranslation}
                      disabled={translationLoading}
                      className={`${INLINE_TRANSLATE_BUTTON_CLASS} transition active:scale-[0.98] ${
                        translationLoading ? "cursor-wait opacity-80" : ""
                      }`}
                    >
                      <img
                        src="/assets/events/translation_icon.svg"
                        alt=""
                        aria-hidden="true"
                        className="h-[10px] w-[10px] shrink-0"
                      />
                      <span className="truncate">{translateButtonLabel}</span>
                    </button>
                  ) : null}
                </p>
                {canTranslate ? (
                  <p
                    ref={descriptionMeasureRef}
                    aria-hidden="true"
                    className="pointer-events-none invisible absolute inset-x-0 top-0 break-words text-[15px] font-normal text-white [overflow-wrap:anywhere]"
                    style={{ lineHeight: `${DESCRIPTION_LINE_HEIGHT_PX}px` }}
                  >
                    <span ref={descriptionMeasureTextRef}>{previewDescription}</span>
                    <span ref={descriptionMeasureEllipsisRef}>…</span>
                    <span className={INLINE_TRANSLATE_BUTTON_CLASS}>
                      <img
                        src="/assets/events/translation_icon.svg"
                        alt=""
                        aria-hidden="true"
                        className="h-[10px] w-[10px] shrink-0"
                      />
                      <span className="truncate">{translateButtonLabel}</span>
                    </span>
                  </p>
                ) : null}
              </>
            )}

            <div
              data-testid="event-description-preview-meta"
              className="mt-[18px] flex shrink-0 flex-col gap-[9px]"
            >
            {!awaitingConceptDetails && <div className="flex min-h-[24px] items-center gap-[10px]">
              <img src="/assets/events/map_pin.svg" alt="" className="h-[20px] w-[20px] shrink-0" />
              <span
                className={`min-w-0 truncate text-[15px] font-medium leading-[22px] tracking-[-0.6px] text-white ${
                  showDirectionsAction
                    ? "max-w-[calc(100%_-_150px)]"
                    : showMapAction
                      ? "max-w-[calc(100%_-_115px)]"
                      : "max-w-full"
                }`}
              >
                {address}
              </span>
              {showDirectionsAction ? (
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="ml-[5px] inline-flex h-[24px] w-[112px] shrink-0 items-center justify-center rounded-[18px] border border-white/35 bg-white/12 px-[10px] text-center text-[10px] font-bold leading-none text-white/85 shadow-[0_6px_14px_rgba(0,0,0,0.12)] transition active:scale-[0.98]"
                >
                  <span className="truncate">
                    {t("events.details.get_directions", { defaultValue: "Get directions" })}
                  </span>
                </a>
              ) : null}
              {showMapAction ? (
                <button
                  type="button"
                  onClick={stopAndRun(onShowMap)}
                  className="ml-[5px] inline-flex h-[18px] min-w-max shrink-0 items-center justify-center whitespace-nowrap rounded-[18px] border-[1.5px] border-white/35 px-[12px] text-center text-[9px] font-semibold leading-[15px] text-white/55"
                >
                  {t("events.details.show_on_map", { defaultValue: "Show on map" })}
                </button>
              ) : null}
            </div>}

            {!awaitingConceptDetails && <div className="flex h-[20px] items-center gap-[10px]">
              <img src="/assets/events/duration_icon.svg" alt="" className="h-[18px] w-[18px] shrink-0" />
              <span className="min-w-0 truncate text-[15px] font-medium leading-[22px] tracking-[-0.6px] text-white">
                {duration}
              </span>
            </div>}

            <div className="flex h-[20px] items-center gap-[10px]">
              <EventsIcon className="h-[20px] w-[20px] shrink-0 text-white" aria-hidden="true" />
              <span className="min-w-0 truncate text-[15px] font-medium leading-[22px] tracking-[-0.6px] text-white">
                {date}
              </span>
            </div>

            {paymentSummary ? (
              <div className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-[10px] text-white">
                <EventMetaIcon
                  src="/assets/events/fee_type.svg"
                  alt=""
                  decorative
                  className="mt-[1px] h-[20px] w-[20px]"
                />
                <div className="min-w-0 text-[15px] font-medium leading-[22px] tracking-[-0.6px] text-white [overflow-wrap:anywhere]">
                  <span>{paymentSummary.label}: </span>
                  <span className="font-normal">
                    {Array.isArray(paymentSummary.items) && paymentSummary.items.length ? (
                      <WrappedSeparatedText items={paymentSummary.items} />
                    ) : (
                      paymentSummary.value
                    )}
                  </span>
                </div>
              </div>
            ) : null}
            </div>
          </div>

          <div
            data-testid="event-description-preview-actions"
            className="-ml-[1px] -mr-[1px] flex h-[55px] shrink-0 gap-[7px]"
            style={{ marginTop: `${PREVIEW_ACTIONS_MARGIN_TOP_PX}px` }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onExpand();
              }}
              className="flex h-[55px] min-w-0 flex-1 items-center justify-center rounded-[100px] border border-white/20 bg-white px-5 text-center text-[15px] font-semibold leading-4 text-[#3E6FFB] shadow-[0_10px_24px_rgba(0,0,0,0.16)] transition active:scale-[0.98]"
              aria-label={t("event_description_modal.actions.open_event", {
                defaultValue: "Open event",
              })}
            >
              <span className="truncate">
                {t("event_description_modal.actions.open_event", {
                  defaultValue: "Open event",
                })}
              </span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventDescriptionModal;
